/* eslint-disable max-nested-callbacks */
/* eslint-disable react/jsx-no-literals */
import React, { useEffect, useMemo, useRef } from 'react';
import { useQueries } from 'react-query';
import styled from 'styled-components';

import ArrowDown from '@/assets/icons/arrow-down.svg';
import { getInitiativeChildren } from '@/features/canvas/api/initiative-relationships';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { getInitiativeDates } from '../../utils/getInitiativeDates';
import { buildSectionTree, FlattenedTreeNode, flattenTree } from '../../utils/treeUtils';

// Constants matching GanttTimelineSection measurements
const GANTT_PADDING_ABOVE = 6;

// Variable heights for parent vs child rows
const GANTT_PARENT_HEIGHT = 28; // Height for parent rows
const GANTT_CHILD_HEIGHT = 20; // Height for child rows (more compact)

const GANTT_SECTION_PADDING = GANTT_PADDING_ABOVE;

// Helper function to get row height based on depth
const getRowHeight = (depth: number) => (depth > 0 ? GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT);

// Helper function to calculate cumulative position
const calculateCumulativePosition = (items: { depth: number }[], index: number) => {
  let position = GANTT_SECTION_PADDING;
  for (let i = 0; i < index; i += 1) {
    position += getRowHeight(items[i].depth);
  }
  return position;
};

// Title column width
const TITLE_COLUMN_WIDTH = 220;

interface GanttTitleColumnProps {
  allInitiativesForOrphanCheck: FilteredNodes[];
  enhancedFilteredNodes: FilteredNodes[];
  expandedRows: Set<number>;
  getSectionHeight: (sectionId: number) => number;
  isDependencyMode: boolean;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
}

// Helper function to calculate node filter state (matches CanvasNodes logic)
/* eslint-disable @typescript-eslint/no-explicit-any */
const calculateNodeFilterState = (
  node: FilteredNodes,
  filters: any[] | undefined,
  activeFilterIds: number[],
  optionCheckedIds: number[]
) => {
  const activeFilters =
    filters?.filter((f: any) => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

  const doesNodeHaveActiveFilter = activeFilters.some(
    (filter: any) => filter.nodes?.some((n: any) => n.nodeId === node.id) ?? false
  );

  const isNodesOptionChecked = activeFilters.every((filter: any) =>
    filter.selection === 'multiple'
      ? filter.nodes?.some(
          (n: any) => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
        ) ?? false
      : optionCheckedIds.includes(
          filter.nodes?.find((n: any) => n.nodeId === node.id)?.optionId ?? -1
        )
  );

  const isNotInFilter = Boolean(
    (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
      (!doesNodeHaveActiveFilter && activeFilterIds.length > 0)
  );

  const isGreyDueToFilter = activeFilterIds.length > 0 && isNotInFilter;
  return isGreyDueToFilter ? 0.15 : 1;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// Helper function to apply filter colors to a node
const applyFilterColorsToNode = (
  node: Partial<FilteredNodes>,
  filters: components['schemas']['Filter'][],
  activeFilterIds: number[]
): FilteredNodes => {
  // Find active filters
  const activeFilters = filters.filter(f => f.id !== undefined && activeFilterIds.includes(f.id));

  // Check if this node has any filter assignments
  const filterWithNode = activeFilters.find(filter =>
    filter.nodes?.some(n => n.nodeId === node.id)
  );

  if (filterWithNode) {
    const filterNode = filterWithNode.nodes?.find(n => n.nodeId === node.id);
    if (filterNode) {
      // Find the option for this filter node
      const option = filterWithNode.options?.find(o => o.id === filterNode.optionId);
      if (option) {
        return {
          ...node,
          color: option.color || '',
          shape: (filterWithNode.shape as 'circle' | 'square' | 'triangle') || 'circle',
          size: option.size,
        } as FilteredNodes;
      }
    }
  }

  return {
    ...node,
    color: '',
    shape: 'circle',
  } as FilteredNodes;
};

const GanttTitleColumn: React.FC<GanttTitleColumnProps> = ({
  allInitiativesForOrphanCheck,
  enhancedFilteredNodes,
  getSectionHeight,
  isDependencyMode,
  expandedRows,
  setExpandedRows,
}) => {
  const {
    sortedVerticalSections,
    sortedHorizontalSections,
    activeFilterIds,
    optionCheckedIds,
    filters,
    setSelectedNodeId,
    setIsInitiativeDetailDialogOpen,
    roadmapNodes,
    hiddenChildNodeIds,
  } = useEditorContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Toggle expanded state for a row
  const toggleExpanded = (initiativeId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(initiativeId)) {
        newSet.delete(initiativeId);
      } else {
        newSet.add(initiativeId);
      }
      return newSet;
    });
  };

  // Combine sections exactly like other Gantt components
  const allSections = useMemo(
    () => [...sortedVerticalSections, ...sortedHorizontalSections],
    [sortedVerticalSections, sortedHorizontalSections]
  );

  // Use enhanced filtered nodes directly - no global re-adding of hidden children
  const actualEnhancedRoadmapNodes = enhancedFilteredNodes;

  // Get unique initiative IDs for fetching children - use ALL roadmap nodes to enable expansion at all levels
  const initiativeIds = useMemo(() => {
    const ids = new Set<number>();
    roadmapNodes?.forEach(node => {
      if (node.initiatives?.id) {
        ids.add(node.initiatives.id);
      }
    });
    return Array.from(ids);
  }, [roadmapNodes]);

  // Fetch children for all initiatives
  const childrenQueries = useQueries(
    initiativeIds.map(id => ({
      queryKey: ['getInitiativeChildren', id],
      queryFn: () => getInitiativeChildren({ initiativeId: id }),
      enabled: true,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }))
  );

  // Create parent-child relationships map from query data
  const parentChildMap = useMemo(() => {
    const map = new Map<number, components['schemas']['Initiative'][]>();
    childrenQueries.forEach((query, index) => {
      if (query.data && query.data.length > 0) {
        const parentId = initiativeIds[index]; // Use index to correlate with initiativeIds
        map.set(parentId, query.data);
      }
    });
    return map;
  }, [childrenQueries, initiativeIds]);

  // Group initiatives by section using tree structure
  const sectionTreeData = useMemo(() => {
    const result: Record<number, (FlattenedTreeNode & { opacity: number })[]> = {};

    allSections.forEach(section => {
      if (section.id !== undefined) {
        // Start with nodes that are naturally in this section
        const baseSectionNodes = actualEnhancedRoadmapNodes.filter(
          node => node.sectionId === section.id && node.initiatives
        );

        // Add hidden children that belong to parents in this section (multi-level)
        const enhancedSectionNodes = [...baseSectionNodes];
        const sectionNodeIds = new Set(baseSectionNodes.map(n => n.id));

        // Keep adding children until no more are found (handles multi-level nesting)
        let nodesToProcess = [...baseSectionNodes];
        const processedIds = new Set(baseSectionNodes.map(n => n.initiatives?.id).filter(Boolean));

        while (nodesToProcess.length > 0) {
          const newlyAdded: FilteredNodes[] = [];

          nodesToProcess.forEach(parentNode => {
            if (parentNode.initiatives?.id) {
              const childrenOfParent = parentChildMap.get(parentNode.initiatives.id) || [];

              childrenOfParent.forEach(childInitiative => {
                if (
                  childInitiative.id &&
                  !processedIds.has(childInitiative.id) &&
                  !sectionNodeIds.has(childInitiative.id)
                ) {
                  const childNodeInRoadmap = roadmapNodes?.find(
                    n => n.initiatives?.id === childInitiative.id
                  );

                  if (
                    childNodeInRoadmap &&
                    hiddenChildNodeIds.has(childNodeInRoadmap.id!) &&
                    childNodeInRoadmap.isVisible !== false
                  ) {
                    // Add the hidden child to this section's nodes
                    const enhancedChild = applyFilterColorsToNode(
                      childNodeInRoadmap,
                      filters || [],
                      activeFilterIds
                    );
                    const addedNode = {
                      ...enhancedChild,
                      color: enhancedChild.color || '',
                      shape: (enhancedChild.shape as 'circle' | 'square' | 'triangle') || 'circle',
                      size: enhancedChild.size || undefined,
                      isVisible: true,
                    };
                    enhancedSectionNodes.push(addedNode);
                    newlyAdded.push(addedNode);
                    sectionNodeIds.add(childNodeInRoadmap.id!);
                    processedIds.add(childInitiative.id);
                  }
                }
              });
            }
          });

          nodesToProcess = newlyAdded; // Process newly added nodes for THEIR children
        }

        // Build tree structure using enhanced section nodes
        const tree = buildSectionTree(enhancedSectionNodes, parentChildMap);

        // Flatten the tree based on expanded state
        const flattened = flattenTree(tree, expandedRows);

        // The tree is already filtered by expandedRows - children only appear when parent is expanded
        // Don't apply KON-145 filtering here - Gantt tree should show children when expanded
        const processedNodes = flattened.map(item => {
          const { node } = item;

          // Calculate opacity using helper function (matches CanvasNodes logic)
          let opacity = calculateNodeFilterState(node, filters, activeFilterIds, optionCheckedIds);

          if (isDependencyMode) {
            const nodeId = node.id;
            const initiativeId = node.initiatives?.id;
            const isNodeHidden = (checkNode: FilteredNodes) => {
              const { startDate, endDate } = getInitiativeDates(checkNode.initiatives);
              return !startDate && !endDate;
            };
            const matchingNode = allInitiativesForOrphanCheck.find(
              n => n.id === nodeId || n.initiatives?.id === initiativeId
            );
            const hasVisibleOutgoing =
              matchingNode?.dependencies &&
              matchingNode.dependencies.length > 0 &&
              matchingNode.dependencies.some(dep => {
                const target = allInitiativesForOrphanCheck.find(
                  n => n.id === dep.slaveNodeId || n.initiatives?.id === dep.slaveNodeId
                );
                return target && !isNodeHidden(target);
              });
            const hasVisibleIncoming = allInitiativesForOrphanCheck.some(other => {
              if (isNodeHidden(other)) return false;
              return other.dependencies?.some(
                dep =>
                  dep.masterNodeId === nodeId ||
                  dep.masterNodeId === initiativeId ||
                  dep.slaveNodeId === nodeId ||
                  dep.slaveNodeId === initiativeId
              );
            });
            if (!hasVisibleOutgoing && !hasVisibleIncoming) {
              opacity = 0.15;
            }
          }

          return {
            ...item,
            opacity,
          };
        });

        result[section.id] = processedNodes;
      }
    });

    return result;
  }, [
    actualEnhancedRoadmapNodes,
    allSections,
    activeFilterIds,
    optionCheckedIds,
    filters,
    parentChildMap,
    expandedRows,
    roadmapNodes,
    hiddenChildNodeIds,
    isDependencyMode,
    allInitiativesForOrphanCheck,
  ]);

  // Calculate total height and force container to be tall enough for scrolling
  useEffect(() => {
    if (containerRef.current) {
      const totalHeight = allSections.reduce((height, section) => {
        if (section.id === undefined) return height;
        const titleItems = sectionTreeData[section.id] || [];
        const SWIMLANE_MIN_HEIGHT = 97.06;
        const calculatedHeight =
          titleItems.length > 0
            ? titleItems.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
              GANTT_SECTION_PADDING
            : SWIMLANE_MIN_HEIGHT;
        // Use the sidebar's calculated height, or fall back to our calculation
        const sidebarHeight = getSectionHeight(section.id);
        const sectionHeight = Math.max(calculatedHeight, sidebarHeight, SWIMLANE_MIN_HEIGHT);

        return height + sectionHeight;
      }, 0);

      // Ensure the container is tall enough to require scrolling
      containerRef.current.style.minHeight = `${Math.max(totalHeight, 800)}px`;
    }
  }, [allSections, sectionTreeData, getSectionHeight]);

  const handleTitleClick = (nodeId: number) => {
    setSelectedNodeId(nodeId);
    setIsInitiativeDetailDialogOpen(true);
  };

  return (
    <TitleColumnContainer ref={containerRef}>
      {allSections.map((section, sectionIndex) => {
        if (section.id === undefined) return null;

        const treeItems = sectionTreeData[section.id] || [];
        const isLastSection = sectionIndex === allSections.length - 1;

        const SWIMLANE_MIN_HEIGHT = 97.06;
        const calculatedHeight =
          treeItems.length > 0
            ? treeItems.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
              GANTT_SECTION_PADDING
            : SWIMLANE_MIN_HEIGHT;
        const sidebarHeight = getSectionHeight(section.id);
        const finalSectionHeight = Math.max(calculatedHeight, sidebarHeight, SWIMLANE_MIN_HEIGHT);

        return (
          <TitleSection
            key={section.id}
            nodeCount={treeItems.length}
            sectionHeight={finalSectionHeight}
          >
            {treeItems.map((item, index) => {
              const { node } = item;
              const title = node.initiatives?.title || 'Untitled Initiative';
              const initiativeId = node.initiatives?.id;
              const topPosition = calculateCumulativePosition(treeItems, index);
              const rowHeight = getRowHeight(item.depth);

              return (
                <TitleItem
                  key={node.id}
                  itemIndex={index}
                  title={title}
                  opacity={item.opacity}
                  hasChevron={item.hasChildren}
                  depth={item.depth}
                  topPosition={topPosition}
                  rowHeight={rowHeight}
                >
                  {/* Chevron on the left for children (depth > 0) */}
                  {item.hasChildren && initiativeId !== undefined && item.depth > 0 && (
                    <ChevronButton
                      onClick={e => {
                        e.stopPropagation();
                        toggleExpanded(initiativeId);
                      }}
                      aria-label={expandedRows.has(initiativeId) ? 'Collapse' : 'Expand'}
                    >
                      <ChevronIcon
                        src={ArrowDown}
                        alt=""
                        isExpanded={expandedRows.has(initiativeId)}
                      />
                    </ChevronButton>
                  )}
                  <TitleText
                    onClick={() => handleTitleClick(node.id!)}
                    hasChevron={item.hasChildren}
                    depth={item.depth}
                  >
                    {title}
                  </TitleText>
                  {/* Chevron on the right for parents (depth === 0) */}
                  {item.hasChildren && initiativeId !== undefined && item.depth === 0 && (
                    <ChevronButton
                      onClick={e => {
                        e.stopPropagation();
                        toggleExpanded(initiativeId);
                      }}
                      aria-label={expandedRows.has(initiativeId) ? 'Collapse' : 'Expand'}
                    >
                      <ChevronIcon
                        src={ArrowDown}
                        alt=""
                        isExpanded={expandedRows.has(initiativeId)}
                      />
                    </ChevronButton>
                  )}
                </TitleItem>
              );
            })}
            {/* Add horizontal lines matching GanttTimelineSection */}
            {treeItems.length > 0 && (
              <>
                {/* Line above first bar */}
                <HorizontalLine key="line-top" top={0} />
                {/* Lines between bars - positioned using cumulative heights, but skip between parent-child and child-child */}
                {Array.from({ length: Math.max(0, treeItems.length - 1) }, (_, index) => {
                  const currentDepth = treeItems[index]?.depth || 0;
                  const nextDepth = treeItems[index + 1]?.depth || 0;

                  // Skip line if current or next item is a child (depth > 0)
                  if (currentDepth > 0 || nextDepth > 0) {
                    return null;
                  }

                  const linePosition = calculateCumulativePosition(treeItems, index + 1);
                  return <HorizontalLine key={`line-${index}`} top={linePosition} />;
                })}
                {/* Line underneath last initiative when section doesn't fill minimum height */}
                {treeItems.length > 0 && calculatedHeight < finalSectionHeight && (
                  <HorizontalLine
                    key="line-after-last"
                    top={calculateCumulativePosition(treeItems, treeItems.length)}
                  />
                )}
                {/* Bottom line for the last section */}
                {isLastSection && (
                  <HorizontalLine
                    key="line-bottom"
                    top={calculateCumulativePosition(treeItems, treeItems.length)}
                  />
                )}
                {/* Bottom line for sections that don't fill minimum height (except last section) */}
                {!isLastSection && calculatedHeight < finalSectionHeight && (
                  <HorizontalLine key="line-section-bottom" top={finalSectionHeight} />
                )}
              </>
            )}
            {/* Add bottom line for empty sections (except last section) */}
            {treeItems.length === 0 && !isLastSection && (
              <HorizontalLine key="empty-section-line" top={finalSectionHeight} />
            )}
          </TitleSection>
        );
      })}
    </TitleColumnContainer>
  );
};

const TitleColumnContainer = styled.div`
  width: ${TITLE_COLUMN_WIDTH}px;
  min-height: 100%;
  background: ${({ theme }) => theme.palette.brand.white};
  border-right: 1px solid #afafaf;
`;

const TitleSection = styled.div<{ nodeCount: number; sectionHeight: number }>`
  position: relative;
  min-height: ${({ sectionHeight }) => sectionHeight}px;
  background: ${({ theme }) => theme.palette.brand.backgroundSidebar};
`;

const TitleItem = styled.div<{
  depth: number;
  hasChevron: boolean;
  itemIndex: number;
  opacity: number;
  rowHeight: number;
  topPosition: number;
}>`
  position: absolute;
  top: ${({ topPosition }) => topPosition}px;
  left: 0;
  right: 0;
  height: ${({ rowHeight }) => rowHeight}px;
  display: flex;
  align-items: center;
  padding-left: ${({ depth }) => 10 + depth * 20}px; // Base padding + 20px per depth level
  padding-right: ${({ hasChevron }) => (hasChevron ? '4px' : '10px')};
  font-size: 1.4rem;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  background: ${({ depth }) => (depth > 0 ? '#f5f5f5' : 'transparent')};
  opacity: ${({ opacity }) => opacity};
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  margin: 0;
  margin-left: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border-radius: 2px;
`;

const ChevronIcon = styled.img<{ isExpanded: boolean }>`
  width: 10px;
  height: 10px;
  transform: ${({ isExpanded }) => (isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
  transition: transform 0.2s ease;
`;

const TitleText = styled.span<{ depth: number; hasChevron?: boolean }>`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
  flex: 1;
  padding-right: ${({ hasChevron, depth }) => (hasChevron && depth === 0 ? '4px' : '0')};
  padding-left: ${({ hasChevron, depth }) => (hasChevron && depth > 0 ? '4px' : '0')};

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.brand.primary};
  }
`;

const HorizontalLine = styled.div<{ top: number }>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #dfdfdf;
  z-index: 1;
`;

export { GanttTitleColumn, TITLE_COLUMN_WIDTH };
